import { watchlistEntryKey } from '@/features/dashboard/lib/watchlist-entry'
import { Exchange, type Instrument } from '@/types/generated/common/types'
import type { MarketScreenerFrame, ScreenerRow, WatchlistEntry } from '@/types/generated/public_api'

/** The venues the screener aggregates, in the server's canonical (enum) order. */
export const SCREENER_EXCHANGES: readonly Exchange[] = [
  Exchange.EXCHANGE_DERIBIT,
  Exchange.EXCHANGE_HYPERLIQUID,
  Exchange.EXCHANGE_BYBIT,
  Exchange.EXCHANGE_DERIVE,
  Exchange.EXCHANGE_LIGHTER,
]

/**
 * The filter token the server stamps on every frame for this venue filter —
 * mirrors `MarketScreenerStream::filter_token` in oems_v2. `ALL` for no venue or
 * every screener venue, otherwise the selected enum names in canonical order
 * joined by ",". Non-screener venues are ignored.
 */
export function screenerFilterToken(exchanges: readonly Exchange[]): string {
  const selected = SCREENER_EXCHANGES.filter((e) => exchanges.includes(e))
  if (selected.length === 0 || selected.length === SCREENER_EXCHANGES.length) return 'ALL'
  return selected.map((e) => Exchange[e]).join(',')
}

/** The subscribe payload's venue list: canonical order, empty for "every venue". */
export function screenerRequestExchanges(exchanges: readonly Exchange[]): Exchange[] {
  const selected = SCREENER_EXCHANGES.filter((e) => exchanges.includes(e))
  return selected.length === SCREENER_EXCHANGES.length ? [] : selected
}

export interface ScreenerState {
  token: string
  /** Seq of the last applied frame; null until the first snapshot. */
  seq: number | null
  asOfMs: number | null
  rows: ReadonlyMap<string, ScreenerRow>
}

export const emptyScreenerState = (token: string): ScreenerState => ({
  token,
  seq: null,
  asOfMs: null,
  rows: new Map(),
})

export type ScreenerApplyResult =
  | { kind: 'applied'; state: ScreenerState }
  /** Frame for another filter, a stale/duplicate seq, or a delta before any snapshot. */
  | { kind: 'ignored' }
  /** A delta skipped a seq: the table can't be trusted until a new snapshot arrives. */
  | { kind: 'gap' }

/** Group-wise merge: a present group replaces the stored one, an absent group keeps it. */
function mergeRow(prev: ScreenerRow, next: ScreenerRow): ScreenerRow {
  return {
    asset: prev.asset,
    live: next.live ?? prev.live,
    windows: next.windows ?? prev.windows,
    derivs: next.derivs ?? prev.derivs,
    book: next.book ?? prev.book,
    meta: next.meta ?? prev.meta,
    // Present with empty `values` means "no flags" — replacing handles that too.
    flags: next.flags ?? prev.flags,
    venues: next.venues.length > 0 ? next.venues : prev.venues,
  }
}

/**
 * Applies one frame. A snapshot (first frame, or mid-stream after the server
 * rebuilt) resets the table and the seq baseline; a delta must be exactly
 * seq + 1. Rows that didn't change keep their object identity, so memoized
 * cells skip re-rendering.
 */
export function applyScreenerFrame(
  state: ScreenerState,
  frame: MarketScreenerFrame
): ScreenerApplyResult {
  if (frame.filterToken !== state.token) return { kind: 'ignored' }
  const seq = Number(frame.seq)
  const asOfMs = Number(frame.asOfMs)

  if (frame.snapshot) {
    const rows = new Map<string, ScreenerRow>()
    for (const row of frame.rows) rows.set(row.asset, row)
    return { kind: 'applied', state: { token: state.token, seq, asOfMs, rows } }
  }

  if (state.seq === null || seq <= state.seq) return { kind: 'ignored' }
  if (seq !== state.seq + 1) return { kind: 'gap' }

  const rows = new Map(state.rows)
  for (const row of frame.rows) {
    const prev = rows.get(row.asset)
    rows.set(row.asset, prev ? mergeRow(prev, row) : row)
  }
  for (const asset of frame.removedAssets) rows.delete(asset)
  return { kind: 'applied', state: { token: state.token, seq, asOfMs, rows } }
}

/** Every watchlist entry that names this row: each venue's symbol plus the reference. */
export function screenerRowEntries(row: ScreenerRow): WatchlistEntry[] {
  const entries: WatchlistEntry[] = row.venues.map((v) => ({
    exchange: v.exchange,
    symbol: v.symbol,
  }))
  const ref = row.meta
  if (
    ref?.referenceSymbol &&
    !entries.some((e) => e.exchange === ref.referenceExchange && e.symbol === ref.referenceSymbol)
  ) {
    entries.push({ exchange: ref.referenceExchange, symbol: ref.referenceSymbol })
  }
  return entries
}

export function isScreenerRowWatched(row: ScreenerRow, watched: ReadonlySet<string>): boolean {
  return screenerRowEntries(row).some((e) => watched.has(watchlistEntryKey(e.exchange, e.symbol)))
}

/**
 * The screener asset a watchlist entry belongs to, keyed the way the server
 * keys rows (`asset_key` in oems_v2): the enrichment's canonical group, else
 * the base currency. An entry with no loaded instrument is taken as a bare
 * symbol — an option-group placeholder ("BTC") or a Hyperliquid perp.
 */
export function screenerEntryAsset(
  entry: Pick<WatchlistEntry, 'symbol'>,
  instrument: Pick<Instrument, 'baseCurrency' | 'enrichment'> | undefined
): string {
  const asset = instrument
    ? instrument.enrichment?.canonicalGroup || instrument.baseCurrency
    : entry.symbol
  return asset.trim().toUpperCase()
}

/**
 * Every asset a watchlist touches through any of its instruments — dated
 * futures and options included, which the screener's perp rows never name.
 */
export function screenerWatchlistAssets(
  entries: readonly WatchlistEntry[],
  instrumentByKey: ReadonlyMap<string, Instrument>
): Set<string> {
  return new Set(
    entries.map((e) =>
      screenerEntryAsset(e, instrumentByKey.get(watchlistEntryKey(e.exchange, e.symbol)))
    )
  )
}

/**
 * The watchlist after toggling `row`: adding appends the row's reference
 * instrument (falling back to its first venue), removing drops every entry
 * that names the row on any venue. Returns null when there is nothing to add.
 */
export function toggleScreenerRowInWatchlist(
  entries: readonly WatchlistEntry[],
  row: ScreenerRow
): WatchlistEntry[] | null {
  const rowKeys = new Set(
    screenerRowEntries(row).map((e) => watchlistEntryKey(e.exchange, e.symbol))
  )
  const kept = entries.filter((e) => !rowKeys.has(watchlistEntryKey(e.exchange, e.symbol)))
  if (kept.length !== entries.length) return kept

  const ref = row.meta?.referenceSymbol
    ? { exchange: row.meta.referenceExchange, symbol: row.meta.referenceSymbol }
    : row.venues[0]
      ? { exchange: row.venues[0].exchange, symbol: row.venues[0].symbol }
      : null
  return ref ? [...entries, ref] : null
}
